import { create } from 'zustand'

export type WindowId = 'work' | 'thoughts' | 'photography' | 'control'

export interface WindowState {
  id: WindowId
  title: string
  isOpen: boolean
  isMinimized: boolean
  zIndex: number
  position: { x: number; y: number }
  size: { width: number | string; height: number | string }
}

export interface ThemeState {
  isDark: boolean
  brightness: number
  volume: number
  isSoundEnabled: boolean
}

interface DesktopStore {
  // Loading state
  isLoading: boolean
  loadingProgress: number
  setLoading: (loading: boolean) => void
  setLoadingProgress: (progress: number) => void

  // Desktop state (after preloader, before windows)
  isDesktopReady: boolean
  setDesktopReady: (ready: boolean) => void

  // Windows
  windows: WindowState[]
  activeWindowId: WindowId | null
  maxZIndex: number
  
  openWindow: (id: WindowId) => void
  closeWindow: (id: WindowId) => void
  minimizeWindow: (id: WindowId) => void
  focusWindow: (id: WindowId) => void
  updateWindowPosition: (id: WindowId, position: { x: number; y: number }) => void
  updateWindowSize: (id: WindowId, size: { width: number | string; height: number | string }) => void

  // Theme
  theme: ThemeState
  toggleTheme: () => void
  setBrightness: (brightness: number) => void
  setVolume: (volume: number) => void
  toggleSound: () => void
}

const defaultWindows: WindowState[] = [
  {
    id: 'work',
    title: 'Work Life',
    isOpen: false,
    isMinimized: false,
    zIndex: 1,
    position: { x: 50, y: 50 },
    size: { width: '80vw', height: '80vh' },
  },
  {
    id: 'thoughts',
    title: 'Thoughts',
    isOpen: false,
    isMinimized: false,
    zIndex: 1,
    position: { x: 80, y: 60 },
    size: { width: '80vw', height: '80vh' },
  },
  {
    id: 'photography',
    title: 'Photography',
    isOpen: false,
    isMinimized: false,
    zIndex: 1,
    position: { x: 100, y: 50 },
    size: { width: '80vw', height: '80vh' },
  },
  {
    id: 'control',
    title: 'Control Panel',
    isOpen: false,
    isMinimized: false,
    zIndex: 1,
    position: { x: window.innerWidth - 380, y: window.innerHeight - 500 },
    size: { width: 'min(30vw, 400px)', height: 'min(40vh, 450px)' },
  },
]

export const useDesktopStore = create<DesktopStore>((set, get) => ({
  // Loading state
  isLoading: true,
  loadingProgress: 0,
  setLoading: (loading) => set({ isLoading: loading }),
  setLoadingProgress: (progress) => set({ loadingProgress: progress }),

  // Desktop state
  isDesktopReady: false,
  setDesktopReady: (ready) => set({ isDesktopReady: ready }),

  // Windows
  windows: defaultWindows,
  activeWindowId: null,
  maxZIndex: 1,

  openWindow: (id) =>
    set((state) => {
      const newMaxZ = state.maxZIndex + 1
      return {
        windows: state.windows.map((w) =>
          w.id === id ? { ...w, isOpen: true, isMinimized: false, zIndex: newMaxZ } : w
        ),
        activeWindowId: id,
        maxZIndex: newMaxZ,
      }
    }),

  closeWindow: (id) =>
    set((state) => ({
      windows: state.windows.map((w) =>
        w.id === id ? { ...w, isOpen: false } : w
      ),
      activeWindowId: state.activeWindowId === id ? null : state.activeWindowId,
    })),

  minimizeWindow: (id) =>
    set((state) => ({
      windows: state.windows.map((w) =>
        w.id === id ? { ...w, isMinimized: true } : w
      ),
      activeWindowId: state.activeWindowId === id ? null : state.activeWindowId,
    })),

  focusWindow: (id) =>
    set((state) => {
      const newMaxZ = state.maxZIndex + 1
      return {
        windows: state.windows.map((w) =>
          w.id === id ? { ...w, zIndex: newMaxZ, isMinimized: false } : w
        ),
        activeWindowId: id,
        maxZIndex: newMaxZ,
      }
    }),

  updateWindowPosition: (id, position) =>
    set((state) => ({
      windows: state.windows.map((w) =>
        w.id === id ? { ...w, position } : w
      ),
    })),

  updateWindowSize: (id, size) =>
    set((state) => ({
      windows: state.windows.map((w) => (w.id === id ? { ...w, size } : w)),
    })),

  // Theme
  theme: {
    isDark: true,
    brightness: 100,
    volume: 50,
    isSoundEnabled: true,
  },

  toggleTheme: () =>
    set((state) => ({
      theme: { ...state.theme, isDark: !state.theme.isDark },
    })),

  setBrightness: (brightness) =>
    set((state) => ({
      theme: { ...state.theme, brightness },
    })),

  setVolume: (volume) =>
    set((state) => ({
      theme: { ...state.theme, volume },
    })),

  toggleSound: () =>
    set((state) => ({
      theme: { ...state.theme, isSoundEnabled: !state.theme.isSoundEnabled },
    })),
}))
